// Contexto de autenticação — sessão via cookie httpOnly (bv_token).
// O cookie viaja sozinho no fetch (same-origin); aqui só guardamos o usuário.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type AuthUser } from './api';

interface AuthCtx {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Bootstrap: se o cookie ainda vale, /me devolve o usuário
    api.me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));

    // api.ts dispara este evento em qualquer 401 → derruba a sessão local
    const onUnauthorized = () => setUser(null);
    window.addEventListener('bv:unauthorized', onUnauthorized);
    return () => window.removeEventListener('bv:unauthorized', onUnauthorized);
  }, []);

  const login = async (email: string, senha: string) => {
    const r = await api.login(email, senha);
    setUser(r.user);
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
